import { Injectable } from '@angular/core';
import { ApiHttpClient, ListOption, ListOptionRequest } from '../modules/tcm-core.module';
import { WarrantyValidationEnum } from '../actions/sales-order/models/warranty-validation-enum';
import { OrderStatusEnum } from '../models/enums';
import { PaymentPlanSalesOrderInfoOption } from '../models/payment-plans';
import { PatientSalesOrderListOptionRequest, ReceiveReturnsSearchRequest, ReturnSalesOrderLinesViewModel, SalesOrderCancelRequest, SalesOrderExchangeCancelRequest, SalesOrderExchangeReturnRequest, SalesOrderFinancedSalePostRequest, SalesOrderFinancedSaleValidationResultViewModel, SalesOrderFormSaveResponse, SalesOrderForPaymentOptionRequest, SalesOrderLineByProductGroupTypeRequest, SalesOrderLineByProductGroupTypeResponse, SalesOrderLineDeleteResponse, SalesOrderLineForNewSerializedItemViewModel, SalesOrderLineFromSerializedItemsLinkedSearchRequest, SalesOrderLineFromSerializedItemsViewModel, SalesOrderLineLinkedSearchHearingAidAddBenefitRequest, SalesOrderLineLinkedSearchLineStatusUpdateRequest, SalesOrderLineMaxAmounts, SalesOrderLinesUpdateDiscountRequest, SalesOrderLineUpdateUnitsRequest, SalesOrderPostRequest, SalesOrderRefundViewModel, SalesOrderReturnRequest, SalesOrderSearchLineViewModel, SalesOrderValidationRequest, SalesOrderValidationResultViewModel, SalesOrderViewModel, SalesOrderVoidRequest, SalesOrderVoidResponse, SalesOrderLineDetailsSaveResponse, SalesOrderAllPostRequest, SalesOrderProviderValidationRequest, SalesOrderFittingPostRequest } from '../models/sales-orders';
import { DeleteSalesOrderLineRequest } from '../models/sales-orders/sales-order-lines/delete-sales-order-line-request';
import { InventoryOrderLinesResponse } from '../models/sales-orders/sales-order-lines/inventory-order-lines-response';
import { SalesOrderLineSerializedItemRequest } from '../models/sales-orders/sales-order-lines/sales-order-line-serialized-item-request';
import { SalesOrderLinesDispensedValidationRequest } from '../models/sales-orders/sales-order-lines/sales-order-lines-dispensed-validation-request';
import { SalesOrderLinesDispensedValidationResponse } from '../models/sales-orders/sales-order-lines/sales-order-lines-dispensed-validation-response';
import { SalesOrderLinesUpdateDiscountResponse } from '../models/sales-orders/sales-order-lines/sales-order-lines-update-discount-response';
import { SalesOrderLinesValidationRequest } from '../models/sales-orders/sales-order-lines/sales-order-lines-validation-request';
import { SalesOrderLinesValidationResponse } from '../models/sales-orders/sales-order-lines/sales-order-lines-validation-response';
import { SalesOrderServicePlan } from '../models/service-plans/sales-order-service-plan';

@Injectable({
  providedIn: 'root'
})
export class SalesOrderDataService {

  constructor(private readonly httpClient: ApiHttpClient) {
  }

  getSalesOrderOptionsAsync(query: string, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('SalesOrders/Options', request, cancellationToken);
  }

  getPatientSalesOrderOptionsAsync(query: string, patientId: number, cancellationToken: Promise<any>): Promise<PaymentPlanSalesOrderInfoOption[]> {
    const request: PatientSalesOrderListOptionRequest = { searchString: query, patientId: patientId };
    return this.httpClient.getJsonAsync<PaymentPlanSalesOrderInfoOption[]>('SalesOrders/GetPatientSalesOrderOptions', request, cancellationToken);
  }

  deleteSalesOrderLineAsync(id: number, isDispensedLine: boolean, timeout: Promise<any>): Promise<SalesOrderLineDeleteResponse> {
    const request: DeleteSalesOrderLineRequest = { salesOrderLineId: id, isDispensedLine: isDispensedLine };
    return this.httpClient.postJsonAsync('SalesOrderLines/Delete', request, timeout);
  }

  postSaleOrderAsync(saleOrderId: number, cancellationToken: Promise<any>): Promise<any> {
    const request = {} as SalesOrderPostRequest;
    request.saleOrderId = saleOrderId;
    return this.httpClient.postJsonAsync('SalesOrders/PostSaleOrder', request, cancellationToken);
  }

  changeToNoSaleStatusAsync(salesOrderId: number, cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('SalesOrders/ChangeToNoSaleStatus', salesOrderId, cancellationToken, false);
  }

  validatePostSaleOrderAsync(saleOrderId: number, cancellationToken: Promise<any>): Promise<SalesOrderValidationResultViewModel> {
    const request = {} as SalesOrderPostRequest;
    request.saleOrderId = saleOrderId;
    return this.httpClient.postJsonAsync<SalesOrderValidationResultViewModel>('SalesOrders/ValidatePostSaleOrder', request, cancellationToken, false);
  }

  validateFinancedSalePostAsync(saleOrderId: number, financingTypeId: number, cancellationToken: Promise<any>): Promise<SalesOrderFinancedSaleValidationResultViewModel> {
    const request: SalesOrderFinancedSalePostRequest = { saleOrderId: saleOrderId, financingTypeId: financingTypeId };
    return this.httpClient.postJsonAsync<SalesOrderFinancedSaleValidationResultViewModel>('SalesOrders/ValidateFinancedSalePost', request, cancellationToken, false);
  }

  returnSaleOrderAsync(request: SalesOrderReturnRequest, timeout: Promise<any>): Promise<SalesOrderLinesValidationResponse> {
    return this.httpClient.postJsonAsync('SalesOrders/ReturnSaleOrder', request, timeout);
  }

  voidSaleOrderAsync(request: SalesOrderVoidRequest, timeout: Promise<any>): Promise<SalesOrderVoidResponse> {
    return this.httpClient.postJsonAsync('SalesOrders/VoidSaleOrder', request, timeout);
  }

  cancelSaleOrderAsync(request: SalesOrderCancelRequest, timeout: Promise<any>): Promise<SalesOrderLinesValidationResponse> {
    return this.httpClient.postJsonAsync('SalesOrders/CancelSaleOrder', request, timeout);
  }

  exchangeReturnSaleOrderAsync(request: SalesOrderExchangeReturnRequest, timeout: Promise<any>): Promise<SalesOrderFormSaveResponse> {
    return this.httpClient.postJsonAsync('SalesOrders/ExchangeReturn', request, timeout);
  }

  exchangeCancelSaleOrderAsync(request: SalesOrderExchangeCancelRequest, timeout: Promise<any>): Promise<SalesOrderFormSaveResponse> {
    return this.httpClient.postJsonAsync('SalesOrders/ExchangeCancel', request, timeout);
  }

  havePaymentsToRefund(salesOrderId: number, isVoid: boolean, timeout: Promise<any>): Promise<boolean> {
    const request = { salesOrderId: salesOrderId, isVoid: isVoid };
    return this.httpClient.getJsonAsync('SalesOrders/HavePaymentsToRefund', request, timeout, false);
  }

  getPaymentsInfoToRefund(salesOrderId: number, isVoid: boolean, timeout: Promise<any>): Promise<SalesOrderRefundViewModel> {
    const request = { salesOrderId: salesOrderId, isVoid: isVoid };
    return this.httpClient.getJsonAsync('SalesOrders/GetPaymentsInfoToRefund', request, timeout, false);
  }

  updateDiscountsInOrderLineAsync(request: SalesOrderLinesUpdateDiscountRequest, timeout: Promise<any>): Promise<SalesOrderLinesUpdateDiscountResponse> {
    return this.httpClient.postJsonAsync('SalesOrderLines/UpdateDiscountsInSalesOrderLine', request, timeout, false);
  }

  updateSalesOrderLineUnitsAsync(salesOrderLineId: number, units: number, isDispensedLine: boolean, timeout: Promise<any>): Promise<SalesOrderLineDetailsSaveResponse> {
    const request: SalesOrderLineUpdateUnitsRequest = {
      salesOrderLineId: salesOrderLineId,
      units: units,
      isDispensedLine: isDispensedLine
    };

    return this.httpClient.postJsonAsync('SalesOrderLines/UpdateSalesOrderLineUnits', request, timeout, false);
  }

  getIfPriceOrBenefitChanged(salesOrderId: number, timeout: Promise<any>): Promise<boolean> {
    const request = { salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync<boolean>('SalesOrderLines/GetIfPriceOrBenefitChanged', request, timeout, false);
  }

  getIfLastMedicalReportInvalidAsync(patientId: number, salesOrderId: number, timeout: Promise<any>): Promise<boolean> {
    const request = { patientId: patientId, salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync<boolean>('SalesOrders/GetLastMedicalReportInvalid', request, timeout, false);
  }

  getGetMaxAmounts(salesOrderLineId: number, isDispensingLine: boolean, cancellationToken: Promise<any>): Promise<SalesOrderLineMaxAmounts> {
    const request = { salesOrderLineId: salesOrderLineId, isDispensingLine: isDispensingLine };
    return this.httpClient.getJsonAsync<SalesOrderLineMaxAmounts>('SalesOrderLines/GetMaxAmounts', request, cancellationToken, false);
  }

  updatePriceOrBenefitForLines(salesOrderId: number, timeout: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('SalesOrderLines/UpdatePriceOrBenefitForLines', salesOrderId, timeout, false);
  }

  getSaleOrderLinesFromSerializedItemsExistsAsync(clinicId: number, productStyleId: number, inventoryOrderLineId:number, timeout: Promise<any>): Promise<boolean> {
    const request = {} as SalesOrderLineFromSerializedItemsLinkedSearchRequest;
    request.clinicId = clinicId;
    request.productStyleId = productStyleId;
    request.inventoryOrderLineId = inventoryOrderLineId;
    return this.httpClient.getJsonAsync<boolean>('SalesOrderLines/SalesOrderLineFromSerializedItemsExists', request, timeout, false);
  }

  getReceivedRepairsOrderNumberAsync(serialNumber: string, cancellationToken: Promise<any>): Promise<number | null> {
    const request = {} as ReceiveReturnsSearchRequest;
    request.serialNumber = serialNumber;
    return this.httpClient.getJsonAsync<number | null>('SalesOrderLines/GetReceivedRepairsOrderNumber', request, cancellationToken, false);
  }

  changeReceivedRepairsToReadyForDeliveryAsync(serialNumber: string, cancellationToken: Promise<any>): Promise<any> {
    const request = {} as ReceiveReturnsSearchRequest;
    request.serialNumber = serialNumber;
    return this.httpClient.postJsonAsync('SalesOrderLines/ChangeReceivedRepairsToReadyForDelivery', request, cancellationToken, false);
  }

  addSalesOrderLineFromNewSerializedItemAsync(request: SalesOrderLineForNewSerializedItemViewModel, timeout: Promise<any>): Promise<number> {
    return this.httpClient.postJsonAsync('SalesOrderLines/AddSalesOrderLineFromNewSerializedItem', request, timeout);
  }

  addSerializedItemToCancelledOrderLineAsync(request: SalesOrderLineForNewSerializedItemViewModel, timeout: Promise<any>): Promise<number> {
    return this.httpClient.postJsonAsync('SalesOrderLines/AddSerializedItemToCancelledOrderLine', request, timeout);
  }

  updateSerializedItemOnCancelledOrderLineAsync(salesOrderLineId: number, timeout: Promise<any>): Promise<number> {
    return this.httpClient.postJsonAsync('SalesOrderLines/UpdateSerialiedItemOnCancelledOrderLine', salesOrderLineId, timeout);
  }

  addSalesOrderLineFromSerializedItemAsync(request: SalesOrderLineFromSerializedItemsViewModel, timeout: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('SalesOrderLines/AddSalesOrderLineFromSerializedItem', request, timeout, false);
  }

  canUpdateSalesOrderLineStatus(request: SalesOrderLineLinkedSearchLineStatusUpdateRequest, timeout: Promise<any>): Promise<boolean> {
    return this.httpClient.getJsonAsync('SalesOrderLines/CanUpdateSalesOrderLineStatus', request, timeout, false);
  }

  validataFulfillSalesOrder(salesOrderId: number, timeout: Promise<any>): Promise<SalesOrderValidationResultViewModel> {
    const request = {} as SalesOrderValidationRequest;
    request.saleOrderId = salesOrderId;
    return this.httpClient.postJsonAsync<SalesOrderValidationResultViewModel>('SalesOrders/ValidateFulfillSalesOrder', request, timeout);
  }

  putOnTrialLines(request: SalesOrderLineLinkedSearchLineStatusUpdateRequest, timeout: Promise<any>): Promise<SalesOrderLinesValidationResponse> {
    return this.httpClient.postJsonAsync('SalesOrderLines/PutOnTrialLines', request, timeout, false);
  }

  autoAssignICDValues(salesOrderId: number, timeout: Promise<any>): Promise<SalesOrderLinesValidationResponse> {
    return this.httpClient.postJsonAsync('SalesOrderLines/AutoAssignICDValues', salesOrderId, timeout, false);
  }

  deliverLines(request: SalesOrderLineLinkedSearchLineStatusUpdateRequest, timeout: Promise<any>): Promise<SalesOrderLinesValidationResponse> {
    return this.httpClient.postJsonAsync('SalesOrderLines/DeliverLines', request, timeout, false);
  }

  addBenefitToHearingAidAsync(request: SalesOrderLineLinkedSearchHearingAidAddBenefitRequest, timeout: Promise<any>): Promise<number> {
    return this.httpClient.postJsonAsync('SalesOrderLines/AddBenefitToHearingAid', request, timeout, false);
  }

  getSalesOrderLineByProductGroupType(request: SalesOrderLineByProductGroupTypeRequest, timeout: Promise<any>): Promise<SalesOrderLineByProductGroupTypeResponse> {
    return this.httpClient.getJsonAsync('SalesOrderLines/GetSalesOrderLineByProductGroupType', request, timeout, false);
  }

  validateWarrantyProductAsync(productStyleId: number, salesOrderId: number, timeout: Promise<any>): Promise<WarrantyValidationEnum> {
    const request = { productStyleId: productStyleId, salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync('SalesOrderLines/ValidateWarrantyProduct', request, timeout);
  }

  canDeleteSalesOrderLineAsync(salesOrderLineId: number, isDispensedLine: boolean, timeout: Promise<any>): Promise<number> {
    const request = { salesOrderLineId: salesOrderLineId, isDispensedLine: isDispensedLine };
    return this.httpClient.getJsonAsync('SalesOrderLines/CanDeleteSalesOrderLine', request, timeout);
  }

  canDeleteHearingAidInBundle(salesOrderLineId: number, isDispensedLine: boolean, timeout: Promise<any>): Promise<number> {
    const request = { salesOrderLineId: salesOrderLineId, isDispensedLine: isDispensedLine };
    return this.httpClient.getJsonAsync('SalesOrderLines/CanDeleteHearingAidInBundle', request, timeout);
  }

  getOptionsForNoteAsync(query: string, cancellationToken: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('SalesOrders/OptionsForNote', request, cancellationToken);
  }

  getOptionsForPaymentAsync(query: string, patientId: number, clinicId: number, cancellationToken: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: SalesOrderForPaymentOptionRequest = { searchString: query, patientId: patientId, clinicId: clinicId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('SalesOrders/OptionsForPayment', request, cancellationToken);
  }

  restoreSalesOrder(salesOrderId: number, timeout: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('SalesOrders/RestoreSalesOrder', salesOrderId, timeout, false);
  }

  getSalesOrderExtendedLinesAsync(salesOrderId: number, cancellationToken: Promise<any> = null): Promise<SalesOrderSearchLineViewModel[]> {
    return this.httpClient.getJsonAsync('SalesOrders/GetSalesOrderExtendedLines', { salesOrderId }, cancellationToken);
  }

  getSalesOrderActualStatusAsync(salesOrderId: number, cancellationToken: Promise<any> = null): Promise<OrderStatusEnum> {
    return this.httpClient.getJsonAsync('SalesOrders/GetSalesOrderActualStatus', { salesOrderId }, cancellationToken);
  }

  getSalesOrderIsSaleStatusAsync(salesOrderId: number, cancellationToken: Promise<any> = null): Promise<boolean> {
    return this.httpClient.getJsonAsync('SalesOrders/GetSalesOrderIsSaleStatus', { salesOrderId }, cancellationToken);
  }

  createExchangeSalesOrderAsync(request: SalesOrderViewModel, cancellationToken: Promise<any> = null): Promise<SalesOrderFormSaveResponse> {
    return this.httpClient.postJsonAsync('SalesOrders/CreateExchangeSalesOrder', request, cancellationToken);
  }

  validateSalesOrderLinesAsync(request: SalesOrderLinesValidationRequest, cancellationToken: Promise<any>): Promise<SalesOrderLinesValidationResponse> {
    return this.httpClient.postJsonAsync('SalesOrderLines/ValidateSalesOrderLines', request, cancellationToken);
  }

  validateSalesOrderLinesDispensedAsync(request: SalesOrderLinesDispensedValidationRequest, cancellationToken: Promise<any>): Promise<SalesOrderLinesDispensedValidationResponse> {
    return this.httpClient.postJsonAsync('SalesOrderLines/ValidationDispensedSalesOrderLines', request, cancellationToken);
  }

  saveSalesOrderLineSerializedItemAsync(request: SalesOrderLineSerializedItemRequest, cancellationToken: Promise<any> = null): Promise<any> {
    return this.httpClient.postJsonAsync('SalesOrderLines/SaveSalesOrderLineSerializedItem', request, cancellationToken);
  }

  getRequireProcedureCodeConfigurationAsync(salesOrderId: number, cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.getJsonAsync('SalesOrders/GetRequireProcedureCodeConfiguration', { salesOrderId }, cancellationToken, false);
  }

  getRefunds(salesOrderId: number, cancellationToken: Promise<any>): Promise<any> {
    const request = { salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync('SalesOrders/GetRefundsForSalesOrder',request, cancellationToken);
  }

  getFortisRefunds(salesOrderId: number, cancellationToken: Promise<any>): Promise<any> {
    const request = { salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync('SalesOrders/GetFortisRefundsForSalesOrder', request, cancellationToken);
  }

  getFortisVoids(salesOrderId: number, cancellationToken: Promise<any>): Promise<any> {
    const request = { salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync('SalesOrders/GetFortisVoidsForSalesOrder', request, cancellationToken);
  }

  getMarketingOptionsForClinicAsync(clinicId: number, marketingCampaignToInclude: number, cancellationToken: Promise<any>): Promise<any> {
    const request = { clinicId: clinicId, marketingCampaignToInclude: marketingCampaignToInclude };
    return this.httpClient.getJsonAsync('SalesOrders/GetMarketingOptionsForClinic', request, cancellationToken);
  }

  InventoryOrderAll(saleOrderId: number, IsOrderIntegrationConfirmed:any, timeout: Promise<any>): Promise<InventoryOrderLinesResponse> {
    const request = {} as SalesOrderAllPostRequest;
    request.salesOrderId = saleOrderId;
    request.isOrderIntegrationConfirmed = IsOrderIntegrationConfirmed;
    return this.httpClient.postJsonAsync('SalesOrderLines/InventoryOrderAll', request, timeout, false);
  }

  validateSalesOrderInsurancePlan(salesOrderId: number, timeout: Promise<any>): Promise<number> {
    const request = { salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync('SalesOrders/ValidateInsurancePlanWithSystemPreferences', request, timeout)
  }
  orderIntegrationRequired(salesOrderId: number, timeout: Promise<any>): Promise<boolean> {
    const request = { salesOrderId: salesOrderId};
    return this.httpClient.getJsonAsync('SalesOrders/OrderIntegrationRequired', request, timeout, false);
  }

  postFittingDate(saleOrderId: number, fittingDateId: number, fittingDateAppointmentOptions: ListOption<number>[], fulfillmentDate: string, fittingDate: string, isFittingDropdownVisible:boolean,
    timeout: Promise<any>): Promise<InventoryOrderLinesResponse> {
    const request = {} as SalesOrderFittingPostRequest;
    request.salesOrderId = saleOrderId;
    request.fittingDateId = fittingDateId;
    request.fittingDateAppointmentOptions = fittingDateAppointmentOptions;
    request.fulfillmentDate = fulfillmentDate;
    request.fittingDate = fittingDate;
    request.isFittingDropdownVisible = isFittingDropdownVisible;
    return this.httpClient.postJsonAsync('SalesOrderLines/PostFittingDate', request, timeout, false);
  }

  validateProviderEnrollmentAndLicensing(salesOrderId: number, providerId: number, insuranceId: number, fromSale: boolean,insurance2Id:number,timeout: Promise<any>): Promise<SalesOrderValidationResultViewModel> {
    const request = {} as SalesOrderProviderValidationRequest;
    request.saleOrderId = salesOrderId;
    request.insuranceId = insuranceId;
    request.providerId = providerId;
    request.fromSale = fromSale;
    request.insurance2Id = insurance2Id;
    return this.httpClient.postJsonAsync<SalesOrderValidationResultViewModel>('SalesOrders/ValidateProviderEnrollmentAndLicensing', request, timeout);
  }

  sendEmailToVendorAsync(request: number[], cancellationToken: Promise<any>): Promise<boolean> {
    return this.httpClient.postJsonAsync('SalesOrderLines/SendEmailToVendor', request, cancellationToken, false);
  }

  servicePlanData(salesOrderId: number, timeout: Promise<any>): Promise<SalesOrderServicePlan[]> {
    const request = { salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync<SalesOrderServicePlan[]>('SalesOrderLines/ServicePlanData', request, timeout);
  }

  isAnyClassicDispensing(salesOrderId: number, timeout: Promise<any>): Promise<boolean> {
    const request = { salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync('SalesOrders/IsAnyClassicDispensing', request, timeout, false);
  }

  isResyncRequiredOnExistingDeposits(salesOrderId: number, timeout: Promise<any>): Promise<boolean> {
    const request = { salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync('SalesOrders/IsResyncRequiredOnExistingDeposits', request, timeout, false);
  }

  getProductStylesOptionsAsync(searchString: string, timeout: Promise<any> = null): Promise<ListOption<number[]>> {
    const request = { searchString: searchString };
    return this.httpClient.getJsonAsync<ListOption<number[]>>('SalesOrders/GetProductStylesOptions', request, timeout);
  }

}
